/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import { EventBusModule } from "@vlinder-web/event-bus-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { BaseApplication } from "./base/baseApplication";
import { StorageModule } from "@vlinder-web/storage-module-react";
import { UnitTest } from "./unit.test";
import { DEFAULT_APP_CONFIG as DEF_CONFIG } from "../config/default.config";
import {
  AppNavigatorService,
  LandingPageService,
  HoldersBatchImportPageService,
  UsersListPageService,
  MasqueraderListPageService,
  PresentationListPageService,
  InviteMasqueraderListPageService,
  SendInviteCommitteePageService,
  SignInPageService,
  BoxDetailPageService,
  PresentationPageService,
  CostumePageService,
  MyProfilePageService,
  CommitteListPageService,
  BulkImportCommitteListPageService,
  OrgProfilePageService,
  CashierPaymentPageService,
  SectionOptionInvPageService,
  InventoryControlPageService,
  MasqueraderReportPageService,
  PurchasedCostumePageService,
  PurchasedCostumeListPageService,
  AboutMePageService,
  CashierPurchasedCostumePageService,
  SwitchRolesPageService,
  ViewBoxDetailService,
  ViewMasqBoxDetailsPageService,
  PaymentSuccessPageService,
  PaymentFailedPageService,
  PaymentFailedFACPageService,
  PaymentPendingPageService,
  BulkImportUserListPageService,
  HoldersBatchCommitteImportPageService,
  SectionBasePriceSummaryPageService,
  CommitteMemberReportPageService,
  DiscountedMasqueraderPageService,
  ZeroPaymentReportService,
  PartialPaymentReportService,
  FullPaymentReportService,
  SalesReportPageService,
  OpenRegisterPageService,
  ManageOpenRegistraionService,
  ProfileUpdateStepperPageService,
  ChangeCostumeOptionsPageService,
  SwitchSectionPagService,
  DeletedBoxService,
  DeletedViewBoxService,
  PaymentTransactionService,
  CostumeInventoryReportPageService,
  PaymentReportPageService,
  SectionOptInvReportPageService,
  OpenRegistrationCostumePageService,
  DownloadCsvService,
  RegisterPageService,
  DiscountReportService,
  CreateMasqBoxPageService,
  PrintLabelPageService,
  ShelfListPageService,
  ScheduleListPageService,
  ScheduleTablePageService,
  ShelfDetailPageService,
  DeliveredReportPageService,
  BlackListUserPageService,
  EmailTemplatePageService,
  MasqInviteEmailTemplatePageService,
  CommitteeInviteEmailTemplatePageService,
  MasqDownPaymentEmailTemplatePageService,
  DeleteBoxEmailTemplatePageService,
  AdminInviteEmailTemplatePageService,
  DiscountInviteEmailTemplateService,
  CashierInviteEmailTemplatePageService,
  CostumePurchaseInviteEmailTemplatePageService,
  InvoiceEmailTemplatePageService,
  TransferBoxEmailTemplatePageService,
  CommonEmailVerificationTemplatePageService,
  TransferBoxNewUserEmailTemplatePageService,
  SettingsService,
  CreateDiscountPageService,
  PreBookInviteEmailTemplatePageService,
  PreBookDownPaymentEmailTemplatePageService,
  InviteesListPageService,
  PartialRemainderEmailTemplatePageService,
  UnPaidRemainderEmailTemplatePageService,
  ButtonSettingsService,
  CashierListPageService,
  CouponReportService,
  CashSupervisorInviteEmailTemplatePageService,
  SupervisorInviteEmailTemplatePageService,
  InvoicePDFEmailTemplatePageService,
  APV2PaymentDetailsPageService,
  BandRouteMapService,
  PurchasedCostumeDetailsPageService,
  MasqBoxListPageService,
  MasqViewBoxPageService,
  GenerateDiscountPageService,
  DiscountListPageService,
  DiscountCouponListService,
  CommitteeDiscountListService,
  DiscountCodeEmailTemplateService,
  MasqInviteWithDiscountEmailTemplateService,
  CostumeOpenRegPageService,
  PresentationImportPageService,
  UpdatedBoxService,
  BlockListUserPageService,
} from "../services";
import toast from "react-hot-toast";
import jwt_decode from "jwt-decode";

export class VApplication extends BaseApplication {
  private static instance: VApplication;

  private constructor(options: any = {}) {
    super(options);

    this.setupBindings();

    //Modules
    this.initModules();

    //Services
    this.initServices();
  }

  static getInstance(options: any = {}) {
    if (!this.instance) {
      this.instance = new VApplication(options);
    }
    return this.instance;
  }

  // @ts-ignore
  async start() {
    await this.initRunLevel();
    await this.unitTest();
  }

  setupBindings() {
    //------------->>>>>--------------//
    const storageService = StorageModule.getInstance();
    const DEFAULT_APP_CONFIG = storageService.get("appConfig", "session");
    //------------->>>>>--------------//

    this.configure("services.SignInPageService", {
      loginRestAPI: DEF_CONFIG?.servers?.rest?.loginServer?.baseURL, // Staging server
      headers: DEFAULT_APP_CONFIG?.headers,
    });

    this.configure("services.RegisterPageService", {
      loginRestAPI: DEF_CONFIG?.servers?.rest?.loginServer?.baseURL,
      headers: DEFAULT_APP_CONFIG?.headers,
      signerPK: DEF_CONFIG?.SIGNER_PK,
    });
  }

  async initRunLevel() {
    //------------->>>>>--------------//
    // const storageSrv = StorageModule.getInstance();
    //const DEFAULT_APP_CONFIG = storageSrv.get("appConfig", "session");
    //------------->>>>>--------------//
    const graphqlModule = this.getSync(
      "services.GraphqlServerModule"
    ) as GraphqlServerModule;
    if (graphqlModule) {
      type TClient = typeof DEF_CONFIG.servers.graphql[0];
      DEF_CONFIG?.servers.graphql.forEach((client: TClient) => {
        graphqlModule
          .createClient(client.name, {
            url: client.url,
            getContext: this.getGraphQLContext.bind(this),
          })
          .then((res) => {
            console.log(`initRunLevel check`, res);
          })
          .catch((err) => {
            console.log(`error in initRunLevel  ${err.message}`);
          });
      });
    }
    //------------->>>>>--------------//
    // const storageService = this.getSync(
    //   "services.StorageModule"
    // ) as StorageModule;
    // await storageService.init();
    //------------->>>>>--------------//
  }

  async unitTest() {
    const ut = new UnitTest(VApplication);
    ut.run();
  }

  initModules() {
    this.service("services.EventBusModule", EventBusModule);
    this.service("services.GraphqlServerModule", GraphqlServerModule);
  }

  initServices() {
    this.service("services.AppNavigatorService", AppNavigatorService);
    this.service("services.LandingPageService", LandingPageService);
    this.service("services.CostumePageService", CostumePageService);
    this.service(
      "services.PurchasedCostumePageService",
      PurchasedCostumePageService
    );
    this.service(
      "services.PurchasedCostumeListPageService",
      PurchasedCostumeListPageService
    );
    this.service(
      "services.PurchasedCostumeDetailsPageService",
      PurchasedCostumeDetailsPageService
    );
    this.service("services.MasqViewBoxPageService", MasqViewBoxPageService);
    this.service("services.SettingsService", SettingsService);
    this.service("services.ButtonSettingsService", ButtonSettingsService);
    this.service("services.BlockListUserPageService", BlockListUserPageService);
    this.service(
      "services.CreateDiscountPageService",
      CreateDiscountPageService
    );
    this.service(
      "services.MasqInviteWithDiscountEmailTemplateService",
      MasqInviteWithDiscountEmailTemplateService
    );
    this.service("services.MasqBoxListPageService", MasqBoxListPageService);
    this.service(
      "services.CostumeOpenRegPageService",
      CostumeOpenRegPageService
    );

    this.service("services.UsersListPageService", UsersListPageService);
    this.service(
      "services.MasqueraderListPageService",
      MasqueraderListPageService
    );
    this.service(
      "services.CommitteeDiscountListService",
      CommitteeDiscountListService
    );
    this.service(
      "services.DiscountCodeEmailTemplateService",
      DiscountCodeEmailTemplateService
    );
    this.service(
      "services.GenerateDiscountPageService",
      GenerateDiscountPageService
    );
    this.service(
      "services.DiscountCouponListService",
      DiscountCouponListService
    );
    this.service("services.DiscountListPageService", DiscountListPageService);
    this.service(
      "services.PresentationListPageService",
      PresentationListPageService
    );
    this.service("services.DiscountReportService", DiscountReportService);
    this.service("services.CouponReportService", CouponReportService);
    this.service(
      "services.MasqueraderListPageService",
      MasqueraderListPageService
    );
    this.service(
      "services.SectionOptInvReportPageService",
      SectionOptInvReportPageService
    );
    this.service(
      "services.PresentationListPageService",
      PresentationListPageService
    );
    this.service("services.InviteesListPageService", InviteesListPageService);

    this.service("services.SettingsService", SettingsService);
    this.service(
      "services.HoldersBatchImportPageService",
      HoldersBatchImportPageService
    );
    this.service("services.UsersListPageService", UsersListPageService);
    this.service("services.CashierListPageService", CashierListPageService);
    this.service(
      "services.MasqueraderListPageService",
      MasqueraderListPageService
    );
    this.service(
      "services.PresentationListPageService",
      PresentationListPageService
    );
    this.service("services.DiscountReportService", DiscountReportService);
    this.service(
      "services.MasqueraderListPageService",
      MasqueraderListPageService
    );
    this.service(
      "services.SectionOptInvReportPageService",
      SectionOptInvReportPageService
    );
    this.service(
      "services.PresentationListPageService",
      PresentationListPageService
    );
    this.service("services.InviteesListPageService", InviteesListPageService);

    this.service(
      "services.InviteMasqueraderListPageService",
      InviteMasqueraderListPageService
    );
    this.service(
      "services.SendInviteCommitteePageService",
      SendInviteCommitteePageService
    );
    this.service(
      "services.InviteMasqueraderListPageService",
      InviteMasqueraderListPageService
    );
    this.service(
      "services.SendInviteCommitteePageService",
      SendInviteCommitteePageService
    );
    this.service("services.BoxDetailPageService", BoxDetailPageService);
    this.service("services.CommitteListPageService", CommitteListPageService);
    this.service("services.SignInPageService", SignInPageService);
    this.service("services.RegisterPageService", RegisterPageService);
    this.service("services.PresentationPageService", PresentationPageService);
    this.service("services.MyProfilePageService", MyProfilePageService);
    this.service(
      "services.BulkImportCommitteListPageService",
      BulkImportCommitteListPageService
    );
    this.service(
      "services.BulkImportCommitteListPageService",
      BulkImportCommitteListPageService
    );
    this.service("services.OrgProfilePageService", OrgProfilePageService);
    this.service(
      "services.CashierPaymentPageService",
      CashierPaymentPageService
    );
    this.service(
      "services.CashSupervisorInviteEmailTemplatePageService",
      CashSupervisorInviteEmailTemplatePageService
    );
    this.service(
      "services.SupervisorInviteEmailTemplatePageService",
      SupervisorInviteEmailTemplatePageService
    );
    this.service(
      "services.SectionOptionInvPageService",
      SectionOptionInvPageService
    );
    this.service("services.ShelfListPageService", ShelfListPageService);
    this.service("services.ScheduleListPageService", ScheduleListPageService);
    this.service("services.ShelfDetailPageService", ShelfDetailPageService);
    this.service("services.ScheduleTablePageService", ScheduleTablePageService);
    this.service(
      "services.InventoryControlPageService",
      InventoryControlPageService
    );
    this.service(
      "services.InviteMasqueraderListPageService",
      InviteMasqueraderListPageService
    );
    this.service(
      "services.SendInviteCommitteePageService",
      SendInviteCommitteePageService
    );
    this.service(
      "services.InviteMasqueraderListPageService",
      InviteMasqueraderListPageService
    );
    this.service(
      "services.SendInviteCommitteePageService",
      SendInviteCommitteePageService
    );
    this.service("services.BoxDetailPageService", BoxDetailPageService);
    this.service("services.CommitteListPageService", CommitteListPageService);
    this.service("services.SignInPageService", SignInPageService);
    this.service("services.RegisterPageService", RegisterPageService);
    this.service("services.PresentationPageService", PresentationPageService);
    this.service("services.MyProfilePageService", MyProfilePageService);
    this.service(
      "services.APV2PaymentDetailsPageService",
      APV2PaymentDetailsPageService
    );
    this.service(
      "services.BulkImportCommitteListPageService",
      BulkImportCommitteListPageService
    );
    this.service(
      "services.BulkImportCommitteListPageService",
      BulkImportCommitteListPageService
    );
    this.service("services.OrgProfilePageService", OrgProfilePageService);
    this.service(
      "services.CashierPaymentPageService",
      CashierPaymentPageService
    );
    this.service(
      "services.SectionOptionInvPageService",
      SectionOptionInvPageService
    );
    this.service("services.ShelfListPageService", ShelfListPageService);
    this.service("services.ScheduleListPageService", ScheduleListPageService);
    this.service("services.ShelfDetailPageService", ShelfDetailPageService);
    this.service("services.ScheduleTablePageService", ScheduleTablePageService);
    this.service(
      "services.InventoryControlPageService",
      InventoryControlPageService
    );

    this.service(
      "services.MasqueraderReportPageService",
      MasqueraderReportPageService
    );
    this.service("services.AboutMePageService", AboutMePageService);
    this.service(
      "services.CashierPurchasedCostumePageService",
      CashierPurchasedCostumePageService
    );
    this.service("services.SwitchRolesPageService", SwitchRolesPageService);
    this.service("services.ViewBoxDetailService", ViewBoxDetailService);
    this.service(
      "services.ViewMasqBoxDetailsPageService",
      ViewMasqBoxDetailsPageService
    );
    this.service(
      "services.PaymentSuccessPageService",
      PaymentSuccessPageService
    );
    this.service("services.PaymentFailedPageService", PaymentFailedPageService);
    this.service(
      "services.PaymentFailedFACPageService",
      PaymentFailedFACPageService
    );
    this.service(
      "services.PaymentPendingPageService",
      PaymentPendingPageService
    );
    this.service(
      "services.OpenRegistrationCostumePageService",
      OpenRegistrationCostumePageService
    );
    this.service(
      "services.ViewMasqBoxDetailsPageService",
      ViewMasqBoxDetailsPageService
    );
    this.service(
      "services.PaymentSuccessPageService",
      PaymentSuccessPageService
    );
    this.service("services.PaymentFailedPageService", PaymentFailedPageService);
    this.service(
      "services.PaymentFailedFACPageService",
      PaymentFailedFACPageService
    );
    this.service(
      "services.PaymentPendingPageService",
      PaymentPendingPageService
    );
    this.service(
      "services.ViewMasqBoxDetailsPageService",
      ViewMasqBoxDetailsPageService
    );
    this.service(
      "services.BulkImportUserListPageService",
      BulkImportUserListPageService
    );
    this.service(
      "services.HoldersBatchCommitteImportPageService",
      HoldersBatchCommitteImportPageService
    );
    this.service(
      "services.SectionBasePriceSummaryPageService",
      SectionBasePriceSummaryPageService
    );
    this.service(
      "services.CostumeInventoryReportPageService",
      CostumeInventoryReportPageService
    );
    this.service(
      "services.CommitteMemberReportPageService",
      CommitteMemberReportPageService
    );
    this.service(
      "services.DiscountedMasqueraderPageService",
      DiscountedMasqueraderPageService
    );
    this.service("services.ZeroPaymentReportService", ZeroPaymentReportService);
    this.service(
      "services.PartialPaymentReportService",
      PartialPaymentReportService
    );
    this.service("services.FullPaymentReportService", FullPaymentReportService);
    this.service(
      "services.DeliveredReportPageService",
      DeliveredReportPageService
    );
    this.service(
      "services.InvoicePDFEmailTemplatePageService",
      InvoicePDFEmailTemplatePageService
    );
    this.service("services.SalesReportPageService", SalesReportPageService);
    this.service("services.OpenRegisterPageService", OpenRegisterPageService);
    this.service(
      "services.ManageOpenRegistraionService",
      ManageOpenRegistraionService
    );
    this.service(
      "services.ProfileUpdateStepperPageService",
      ProfileUpdateStepperPageService
    );
    this.service(
      "services.PresentationImportPageService",
      PresentationImportPageService
    );
    this.service("services.UpdatedBoxService", UpdatedBoxService);
    this.service(
      "services.ChangeCostumeOptionsPageService",
      ChangeCostumeOptionsPageService
    );
    this.service("services.BandRouteMapService", BandRouteMapService);
    this.service("services.SwitchSectionPagService", SwitchSectionPagService);
    this.service("services.DeletedBoxService", DeletedBoxService);
    this.service("services.DeletedViewBoxService", DeletedViewBoxService);
    this.service(
      "services.PaymentTransactionService",
      PaymentTransactionService
    );
    this.service("services.DownloadCsvService", DownloadCsvService);
    this.service("services.CreateMasqBoxPageService", CreateMasqBoxPageService);
    this.service("services.PrintLabelPageService", PrintLabelPageService);
    this.service("services.BlackListUserPageService", BlackListUserPageService);
    this.service("services.EmailTemplatePageService", EmailTemplatePageService);
    this.service(
      "services.MasqInviteEmailTemplatePageService",
      MasqInviteEmailTemplatePageService
    );
    this.service(
      "services.CommitteeInviteEmailTemplatePageService",
      CommitteeInviteEmailTemplatePageService
    );
    this.service(
      "services.MasqDownPaymentEmailTemplatePageService",
      MasqDownPaymentEmailTemplatePageService
    );
    this.service(
      "services.DeleteBoxEmailTemplatePageService",
      DeleteBoxEmailTemplatePageService
    );
    this.service(
      "services.AdminInviteEmailTemplatePageService",
      AdminInviteEmailTemplatePageService
    );
    this.service(
      "services.DiscountInviteEmailTemplateService",
      DiscountInviteEmailTemplateService
    );
    this.service(
      "services.CashierInviteEmailTemplatePageService",
      CashierInviteEmailTemplatePageService
    );
    this.service(
      "services.InviteMasqueraderListPageService",
      InviteMasqueraderListPageService
    );
    this.service("services.PaymentReportPageService", PaymentReportPageService);
    this.service(
      "services.SendInviteCommitteePageService",
      SendInviteCommitteePageService
    );
    this.service(
      "services.InviteMasqueraderListPageService",
      InviteMasqueraderListPageService
    );
    this.service(
      "services.SendInviteCommitteePageService",
      SendInviteCommitteePageService
    );
    this.service("services.BoxDetailPageService", BoxDetailPageService);
    this.service("services.CommitteListPageService", CommitteListPageService);
    this.service("services.SignInPageService", SignInPageService);
    this.service("services.RegisterPageService", RegisterPageService);
    this.service("services.PresentationPageService", PresentationPageService);
    this.service("services.MyProfilePageService", MyProfilePageService);
    this.service(
      "services.BulkImportCommitteListPageService",
      BulkImportCommitteListPageService
    );
    this.service(
      "services.BulkImportCommitteListPageService",
      BulkImportCommitteListPageService
    );
    this.service("services.OrgProfilePageService", OrgProfilePageService);
    this.service(
      "services.CashierPaymentPageService",
      CashierPaymentPageService
    );
    this.service(
      "services.SectionOptionInvPageService",
      SectionOptionInvPageService
    );
    this.service("services.ShelfListPageService", ShelfListPageService);
    this.service("services.ScheduleListPageService", ScheduleListPageService);
    this.service("services.ShelfDetailPageService", ShelfDetailPageService);
    this.service("services.ScheduleTablePageService", ScheduleTablePageService);
    this.service(
      "services.InventoryControlPageService",
      InventoryControlPageService
    );
    this.service(
      "services.MasqueraderReportPageService",
      MasqueraderReportPageService
    );
    this.service("services.AboutMePageService", AboutMePageService);
    this.service(
      "services.CashierPurchasedCostumePageService",
      CashierPurchasedCostumePageService
    );
    this.service("services.SwitchRolesPageService", SwitchRolesPageService);
    this.service("services.ViewBoxDetailService", ViewBoxDetailService);
    this.service(
      "services.ViewMasqBoxDetailsPageService",
      ViewMasqBoxDetailsPageService
    );
    this.service(
      "services.PaymentSuccessPageService",
      PaymentSuccessPageService
    );
    this.service("services.PaymentFailedPageService", PaymentFailedPageService);
    this.service(
      "services.PaymentFailedFACPageService",
      PaymentFailedFACPageService
    );
    this.service(
      "services.PaymentPendingPageService",
      PaymentPendingPageService
    );
    this.service(
      "services.PreBookInviteEmailTemplatePageService",
      PreBookInviteEmailTemplatePageService
    );
    this.service(
      "services.ViewMasqBoxDetailsPageService",
      ViewMasqBoxDetailsPageService
    );
    this.service(
      "services.PaymentSuccessPageService",
      PaymentSuccessPageService
    );
    this.service("services.PaymentFailedPageService", PaymentFailedPageService);
    this.service(
      "services.PaymentFailedFACPageService",
      PaymentFailedFACPageService
    );
    this.service(
      "services.PaymentPendingPageService",
      PaymentPendingPageService
    );
    this.service(
      "services.ViewMasqBoxDetailsPageService",
      ViewMasqBoxDetailsPageService
    );
    this.service(
      "services.BulkImportUserListPageService",
      BulkImportUserListPageService
    );
    this.service(
      "services.HoldersBatchCommitteImportPageService",
      HoldersBatchCommitteImportPageService
    );
    this.service(
      "services.SectionBasePriceSummaryPageService",
      SectionBasePriceSummaryPageService
    );
    this.service(
      "services.CostumeInventoryReportPageService",
      CostumeInventoryReportPageService
    );
    this.service(
      "services.CommitteMemberReportPageService",
      CommitteMemberReportPageService
    );
    this.service(
      "services.DiscountedMasqueraderPageService",
      DiscountedMasqueraderPageService
    );
    this.service("services.ZeroPaymentReportService", ZeroPaymentReportService);
    this.service(
      "services.PartialPaymentReportService",
      PartialPaymentReportService
    );
    this.service("services.FullPaymentReportService", FullPaymentReportService);
    this.service(
      "services.DeliveredReportPageService",
      DeliveredReportPageService
    );
    this.service("services.SalesReportPageService", SalesReportPageService);
    this.service("services.OpenRegisterPageService", OpenRegisterPageService);
    this.service(
      "services.ManageOpenRegistraionService",
      ManageOpenRegistraionService
    );
    this.service(
      "services.ProfileUpdateStepperPageService",
      ProfileUpdateStepperPageService
    );
    this.service(
      "services.ChangeCostumeOptionsPageService",
      ChangeCostumeOptionsPageService
    );
    this.service("services.SwitchSectionPagService", SwitchSectionPagService);
    this.service("services.DeletedBoxService", DeletedBoxService);
    this.service("services.DeletedViewBoxService", DeletedViewBoxService);
    this.service(
      "services.PaymentTransactionService",
      PaymentTransactionService
    );
    this.service("services.DownloadCsvService", DownloadCsvService);
    this.service("services.CreateMasqBoxPageService", CreateMasqBoxPageService);
    this.service("services.PrintLabelPageService", PrintLabelPageService);
    this.service("services.BlackListUserPageService", BlackListUserPageService);
    this.service("services.EmailTemplatePageService", EmailTemplatePageService);
    this.service(
      "services.MasqInviteEmailTemplatePageService",
      MasqInviteEmailTemplatePageService
    );
    this.service(
      "services.CommitteeInviteEmailTemplatePageService",
      CommitteeInviteEmailTemplatePageService
    );
    this.service(
      "services.MasqDownPaymentEmailTemplatePageService",
      MasqDownPaymentEmailTemplatePageService
    );
    this.service(
      "services.DeleteBoxEmailTemplatePageService",
      DeleteBoxEmailTemplatePageService
    );
    this.service(
      "services.AdminInviteEmailTemplatePageService",
      AdminInviteEmailTemplatePageService
    );
    this.service(
      "services.DiscountInviteEmailTemplateService",
      DiscountInviteEmailTemplateService
    );
    this.service(
      "services.CashierInviteEmailTemplatePageService",
      CashierInviteEmailTemplatePageService
    );
    this.service(
      "services.CostumePurchaseInviteEmailTemplatePageService",
      CostumePurchaseInviteEmailTemplatePageService
    );

    this.service(
      "services.InvoiceEmailTemplatePageService",
      InvoiceEmailTemplatePageService
    );
    this.service(
      "services.TransferBoxEmailTemplatePageService",
      TransferBoxEmailTemplatePageService
    );
    this.service(
      "services.CommonEmailVerificationTemplatePageService",
      CommonEmailVerificationTemplatePageService
    );
    this.service(
      "services.TransferBoxNewUserEmailTemplatePageService",
      TransferBoxNewUserEmailTemplatePageService
    );
    this.service(
      "services.PreBookDownPaymentEmailTemplatePageService",
      PreBookDownPaymentEmailTemplatePageService
    );
    this.service(
      "services.PartialRemainderEmailTemplatePageService",
      PartialRemainderEmailTemplatePageService
    );
    this.service(
      "services.UnPaidRemainderEmailTemplatePageService",
      UnPaidRemainderEmailTemplatePageService
    );
  }
  private validateToken = (): boolean => {
    const storageService = StorageModule.getInstance();
    const session =
      storageService.get("userProfile", "session") ??
      storageService.get("userProfile", "local");

    const token = session?.accessToken;

    if (token) {
      try {
        const decoded: any = jwt_decode(token);
        const isValid = decoded.exp * 1000 > Date.now();

        if (!isValid) {
          toast.error("Session expired. Please re-login.");
          storageService.reset("session");
          storageService.reset("local");
          return false;
        }

        return true;
      } catch (err) {
        console.error("Token decode error", err);
        toast.error("Invalid token. Please re-login.");
        storageService.reset("session");
        storageService.reset("local");
        return true; // allow flow to continue when token is malformed
      }
    }

    // No access token found — don't block flow
    return true;
  };

  private getGraphQLContext() {
    const loginService = this.getSync(
      "services.AppNavigatorService"
    ) as AppNavigatorService;

    let token = loginService?.getToken();
    if (!this.validateToken()) {
      window.open(`/`, "_self");
      return { headers: {} };
    }
    // //FIXME:

    if (token) {
      return {
        headers: {
          authentication: token,
          headers: {
            // ...{ ...deviceInfoService?.getDeviceInfo() },
            // ...{ ...deviceInfoService?.getAppInfo() },
          },
        },
      };
    } else {
      return {
        headers: {
          headers: {
            // ...{ ...deviceInfoService?.getDeviceInfo() },
            // ...{ ...deviceInfoService?.getAppInfo() },
          },
        },
      };
    }
  }
}
